/**
 * Authentication context provider.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { Session, Identity } from '@ory/client';
import { kratos } from '../lib/kratos';
import { config } from '../config';

export interface Organization {
  id: string;
  name: string;
  role: string;
}

/** Authentication Assurance Level */
export type AAL = 'aal1' | 'aal2' | null;

interface AuthContextValue {
  session: Session | null;
  identity: Identity | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  organizations: Organization[];
  currentOrganization: Organization | null;
  error: string | null;
  /** Current Authentication Assurance Level (aal1 = password only, aal2 = with MFA) */
  aal: AAL;
  /** Whether the user has MFA configured */
  hasMfa: boolean;
  /** Current KYC verification level (0=none, 1=basic, 2=enhanced, 3=full) */
  kycLevel: number;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  switchOrganization: (orgId: string) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(null);
  const [aal, setAal] = useState<AAL>(null);
  const [hasMfa, setHasMfa] = useState(false);
  const [kycLevel, setKycLevel] = useState(0);

  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await fetch(`${config.gatewayUrl}/v1/access/me/organizations`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        const orgs: Organization[] = data.organizations || [];
        setOrganizations(orgs);

        // Validate the saved org ID — if it's no longer in the user's orgs, clear it
        const savedOrgId = localStorage.getItem('orbital_current_org');
        const savedOrgValid = savedOrgId && orgs.some((o) => o.id === savedOrgId);
        if (!savedOrgValid && savedOrgId) {
          localStorage.removeItem('orbital_current_org');
          setCurrentOrgId(null);
        }

        // Set current org from default or first available (only if no valid saved org)
        if (!savedOrgValid) {
          const defaultOrg = data.default_organization;
          if (defaultOrg && orgs.some((o) => o.id === defaultOrg)) {
            setCurrentOrgId(defaultOrg);
          } else if (orgs.length > 0) {
            setCurrentOrgId(orgs[0].id);
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
    }
  }, []);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { data } = await kratos.toSession();
      setSession(data);

      // Extract AAL from session
      const sessionAal = data.authenticator_assurance_level as AAL;
      setAal(sessionAal);

      // Check if MFA is configured by looking at authentication methods
      const authMethods = data.authentication_methods || [];
      const hasTotpOrWebauthn = authMethods.some(
        (method) => method.method === 'totp' || method.method === 'webauthn'
      );
      setHasMfa(hasTotpOrWebauthn);

      // Extract KYC level from identity traits
      const traits = data.identity?.traits as Record<string, unknown> | undefined;
      setKycLevel(typeof traits?.kyc_level === 'number' ? traits.kyc_level : 0);

      // Fetch organizations for the user
      if (data.identity?.id) {
        await fetchOrganizations();
      }
    } catch (err) {
      setSession(null);
      setOrganizations([]);
      setAal(null);
      setHasMfa(false);
      setKycLevel(0);
      // Check for AAL upgrade required (403 with redirect_browser_to)
      if (err && typeof err === 'object' && 'response' in err) {
        const response = (err as { response?: { status?: number; data?: { error?: { details?: { redirect_browser_to?: string } } } } }).response;
        if (response?.status === 403 && response?.data?.error?.details?.redirect_browser_to) {
          // Session exists but needs AAL upgrade - redirect to complete 2FA
          const redirectUrl = response.data.error.details.redirect_browser_to;
          // Convert Kratos API URL to our UI login URL with aal param
          if (redirectUrl.includes('aal=aal2')) {
            window.location.href = '/login?aal=aal2';
            return;
          }
        }
        // Not logged in is not an error
        if (response?.status !== 401) {
          setError('Failed to fetch session');
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [fetchOrganizations]);

  const logout = useCallback(async () => {
    try {
      const { data } = await kratos.createBrowserLogoutFlow();
      window.location.href = data.logout_url;
    } catch (err) {
      console.error('Logout failed:', err);
      setError('Failed to logout');
    }
  }, []);

  const switchOrganization = useCallback((orgId: string) => {
    setCurrentOrgId(orgId);
    // Persist to localStorage
    localStorage.setItem('orbital_current_org', orgId);
  }, []);

  // Initial session check
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Restore current org from localStorage
  useEffect(() => {
    const savedOrg = localStorage.getItem('orbital_current_org');
    if (savedOrg) {
      setCurrentOrgId(savedOrg);
    }
  }, []);

  const identity = session?.identity ?? null;
  const isAuthenticated = !!session?.active;
  const currentOrganization = organizations.find((org) => org.id === currentOrgId) ?? null;

  const value: AuthContextValue = {
    session,
    identity,
    isLoading,
    isAuthenticated,
    organizations,
    currentOrganization,
    error,
    aal,
    hasMfa,
    kycLevel,
    refresh,
    logout,
    switchOrganization,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
